import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Recipe } from '../types/recipe';
import { ThemeCategory, filterRecipesByTheme, getThemeDisplayName } from '../utils/recipeFilters';
import RecipeGrid from '../components/recipe/RecipeGrid';
import RecipeFilters from '../components/recipe/RecipeFilters';
import ScrollToTopButton from '../components/common/ScrollToTopButton';

type Props = {
  recipes: Recipe[];
  theme: ThemeCategory;
  onRecipeSelect: (recipe: Recipe) => void;
  onBack: () => void;
};

export default function ThemeRecipesPage({ recipes, theme, onRecipeSelect, onBack }: Props) {
  const [selectedCategory, setSelectedCategory] = useState<string>('Tout');
  
  const handleRecipeSelect = (recipe: Recipe) => {
    // Save scroll position before navigating
    const scrollPosition = window.scrollY;
    sessionStorage.setItem('themeRecipesScrollPosition', scrollPosition.toString());
    onRecipeSelect(recipe);
  };

  // Always scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter recipes by theme first
  const themeRecipes = filterRecipesByTheme(recipes, theme);
  
  // Then apply additional category filter if needed
  const filteredRecipes = selectedCategory === 'Tout' 
    ? themeRecipes
    : themeRecipes.filter(recipe => {
        switch (selectedCategory) {
          case 'Déjeuner':
            return recipe.category === 'Entrée' || recipe.category === 'repas';
          case 'Dîner':
            return recipe.category === 'Plat Principal' || recipe.category === 'Plat principal';
          case 'Dessert & Gâteaux':
            return recipe.category === 'Dessert';
          default:
            return true;
        }
      });

  const themeDisplayName = getThemeDisplayName(theme);

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">{themeDisplayName}</h1>
          <p className="text-gray-600">
            {filteredRecipes.length} recette{filteredRecipes.length > 1 ? 's' : ''} trouvée{filteredRecipes.length > 1 ? 's' : ''}
          </p>
        </div>
        
        <RecipeFilters
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />
        
        {filteredRecipes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Aucune recette trouvée pour ce thème</p>
            <p className="text-gray-400 text-sm mt-2">
              Essayez de changer les filtres ou revenez plus tard pour de nouvelles recettes
            </p>
          </div>
        ) : (
          <RecipeGrid
            recipes={filteredRecipes}
            onRecipeSelect={handleRecipeSelect}
          />
        )}
      </div>
      <ScrollToTopButton />
    </div>
  );
}